import { useSession, useUserInfo } from "@lohono/react-auth";
import Card from "@mui/material/Card";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import ISPRAVA_LOGO from "../../assets/isprava-white.svg";
import OI_BANNER from "../../assets/login-background.jpg";
import LOHONO_LOGO from "../../assets/lohono-white.svg";
import { useMe } from "../../contexts/me-context";
import { useSecureState } from "../../contexts/secure-state-context";
import { useSnackbar } from "../../contexts/snackbar-provider";
import { useStaffService } from "../../contexts/staff-service-context";
import { Staff } from "../../types/staff";
import useLoginStyles from "./LoginStyle";

function Login() {
  const { isSecure, updateIsSecure } = useSecureState(),
    { updateMe } = useMe(),
    { getMe } = useStaffService(),
    [qp] = useSearchParams(),
    push = useNavigate(),
    enqueueSnackbar = useSnackbar(),
    classNames = useLoginStyles(),
    [role, updateRole] = useState<string>(""),
    [showLoginButton, updateShowLoginButton] = useState<boolean>(false),
    { isAuthenticated, updateSession } = useSession(),
    { userInfo, error } = useUserInfo();

  /**
   * Update the user object in the app state
   * @param me - The user object
   */  
  const onUpdateMe = (me: Staff) => {
    updateMe(me);
    updateIsSecure(true);

    /**
     * saving role in session storage for Agent Access
     */
    sessionStorage.setItem("loginRole", me.role_name ? me.role_name : "");
    sessionStorage.setItem("loginId", `${me.agent_id}`);
    if (me.role_name === "agent") {
      sessionStorage.setItem("agentNumber", `${me.phone_no}`);
    }
  };

  /**
   * Clear URL parameters after processing authentication data
   * This prevents URL length issues and removes sensitive data from URL
   * Note: due_until is kept as it's set by the system
   */
  const clearUrlParameters = () => {
    const url = new URL(window.location.href);
    const paramsToRemove = ['token', 'code', 'userProfile'];
    
    console.log('Current URL before cleanup:', window.location.href);
    console.log('URL search params:', url.searchParams.toString());
    
    paramsToRemove.forEach(param => {
      if (url.searchParams.has(param)) {
        console.log(`Removing parameter: ${param}`);
        url.searchParams.delete(param);
      }
    });
    
    // Replace the current URL without the parameters
    const newUrl = url.pathname + url.search;
    window.history.replaceState({}, document.title, newUrl);
    console.log('URL after cleanup:', newUrl);
  };

  /**
   * Restore the session from the app state
   */
  const restoreSession = async () => {
    const { response: me, error } = await getMe();

    if (error || !me) {
      updateShowLoginButton(true);
      return;
    }

    onUpdateMe(me);
    updateRole(me.role);
  };

  /**
   * Handle the login with Lohono auth
   * @param token - The token from Lohono auth
   */
  const handleLohonoLogin = async (token: string) => {
    try {
      // Set the session token
      updateSession(token);
      
      // Clear URL parameters immediately after setting token
      clearUrlParameters();
      
      // Wait for userInfo to be available, then process it
      const processUserInfo = () => {
        if (userInfo) {
          // Convert userInfo from Lohono auth to Staff format and update the app state
          const staffData: Staff = {
            id: userInfo.id || 0,
            agent_id: userInfo.agent_id || null,
            first_name: userInfo.name?.split(' ')[0] || "",
            last_name: userInfo.name?.split(' ').slice(1).join(' ') || "",
            email: userInfo.email || "",
            handle: userInfo.handle || "",
            role_id: userInfo.role_id || "",
            role: userInfo.role || "",
            role_name: userInfo.role_name || "",
            phone_no: userInfo.mobile && userInfo.mobile !== "null" ? `${userInfo.country_code || ""} ${userInfo.mobile}`.trim() : "",
            poc_head: userInfo.poc_exec || "",
            verticals: userInfo.verticals || [],
            calling_phone_no: userInfo.calling_number || "",
            active: userInfo.active || true,
            acl: new Set(userInfo.acl || []),
            sipCredentials: userInfo.sip_details ? {
              domain: userInfo.sip_details.domain || "",
              ha1: userInfo.sip_details.ha1 || "",
              h1: userInfo.sip_details.h1 || "",
              h1b: userInfo.sip_details.h1b || "",
              username: userInfo.sip_details.username || "",
              password: userInfo.sip_details.password || "",
            } : undefined,
          };

          onUpdateMe(staffData);
          updateRole(userInfo.role || "");
        } else {
          // If userInfo is not available yet, wait a bit and try again
          setTimeout(processUserInfo, 100);
        }
      };
      
      // Start processing user info
      processUserInfo();
    } catch (error) {
      enqueueSnackbar("Login failed", "error");
      updateShowLoginButton(true);
    }
  };

  /**
   * Handle the redirect after authentication
   */
  const onRedirect = () => {
    if (!isSecure) {
      return;
    }

    const intent = role === "Agent" ? "/rentals/trips" : qp.get("intent") ?? "/";

    // Preserve all query parameters except auth-related ones and intent
    const paramsToExclude = ['token', 'code', 'userProfile', 'intent'];
    const preservedParams = new URLSearchParams();
    
    qp.forEach((value, key) => {
      if (!paramsToExclude.includes(key)) {
        preservedParams.append(key, value);
      }
    });
    
    // Build the final URL with preserved parameters
    const finalPath = intent + (preservedParams.toString() ? `?${preservedParams.toString()}` : '');
    push(finalPath, { replace: true });
  };

  // Check for authentication tokens in URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const code = urlParams.get('code');
    const userProfile = urlParams.get('userProfile');
    
    console.log('Full URL:', window.location.href);
    console.log('URL search:', window.location.search);
    console.log('URL parameters found:', { token: !!token, code: !!code, userProfile: !!userProfile });
    console.log('All URL parameters:', Object.fromEntries(urlParams.entries()));
    
    if (token) {
      // If we have a token from Lohono auth, use it to login
      handleLohonoLogin(token);
    } else if (code) {
      // If we have a code, we might need to exchange it for a token
      // For now, try to restore session
      restoreSession();
      // Clear the URL parameters
      clearUrlParameters();
    } else if (userProfile) {
      // If we have userProfile data, it means we're coming from Lohono auth
      // Try to restore session (the userProfile data will be processed by the library)
      restoreSession();
      // Clear the URL parameters
      clearUrlParameters();
    } else {
      // Otherwise, try to restore existing session
      restoreSession();
    }
  }, []);

  // Handle redirect after authentication
  useEffect(() => {
    onRedirect();
  }, [isSecure]);

  // Show login button if not authenticated and no existing session
  useEffect(() => {
    if (!isAuthenticated && !isSecure) {
      updateShowLoginButton(true);
    } else {
      updateShowLoginButton(false);
    }
  }, [isAuthenticated, isSecure]);

  // Handle errors
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, "error");
    }
  }, [error, enqueueSnackbar]);

  if (!showLoginButton) {
    return null;
  }

  return (
    <section className={classNames.login}>
      <div className={classNames.logos}>
        <img
          alt="lohono-logo"
          className={classNames.lohonoLogo}
          src={LOHONO_LOGO}
        />
        +
        <img
          alt="isprava-logo"
          className={classNames.ispravaLogo}
          src={ISPRAVA_LOGO}
        />
      </div>
      <div className={classNames.title}>Platform for Super Human</div>
      <Card className={classNames.loginCard}>
        <div className={classNames.loginContainer}>
          <div className={classNames.subtitle}>
            Login with your Lohono Account
          </div>
          <div className={classNames.loginButton}>
            <button onClick={() => window.location.href = `https://auth.lohono.com?redirect_url=${window.location.href}`}>
              Login with Lohono
            </button>
          </div>
        </div>
      </Card>
      <img
        alt="oi-banner"
        className={classNames.banner}
        src={OI_BANNER}
      />
    </section>
  );
}

export default Login;